import {
  Directive,
  DoCheck,
  Host,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { dateValueFormatter } from '@assetaccountant/core';
import { GridColumnComponent } from '@assetaccountant/core/components/grid/grid-column/grid-column.component';
import {
  currencyValueFormatter,
  decimalValueFormatter,
  finYearValueFormatter,
  percentageValueFormatter,
  transactionTypeValueFormatter,
  yesNoValueFormatter,
} from '@assetaccountant/core/presentations/cellrenderers';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { Subject, Subscription, merge } from 'rxjs';
import { GRID_ROW_DATA, GridRowDataProvider } from './grid-row-data.provider';
import { ROW_DATA, RowDataProvider } from './row-data.directive';
const LETTER_WIDTH = 9;
const NARROW_LETTER_WIDTH = 4;
const CELL_PADDING = 40;
// Common narrow characters to compute at half-width
// Does not need to be exhaustive
const NARROW_CHARS = [
  ' ',
  '.',
  ',',
  '!',
  ':',
  ';',
  '-',
  '/',
  '\\',
  // Braces
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  // Letters
  'f',
  'i',
  'j',
  'l',
  't',
  'I',
  'J',
];

export const getWidthOfCell = (text: string) =>
  [...text].reduce(
    (w, c) => w + (NARROW_CHARS.includes(c) ? NARROW_LETTER_WIDTH : LETTER_WIDTH),
    CELL_PADDING,
  );

export type FormatterType =
  | 'currency'
  | 'date'
  | 'percentage'
  | 'transactionType'
  | 'yesNo'
  | 'finYear'
  | 'decimal'
  | 'disposalMethod';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'app-grid-column[valueFormat]',
  standalone: false,
})
export class ValueFormatDirective implements DoCheck, OnInit, OnDestroy {
  @Input() set valueFormat(value: FormatterType | null | undefined) {
    this.column.valueFormatter = undefined;
    this.updateWidth = () => {};

    if (value && typeof value === 'string') {
      const valueFormatter = this.getValueFormatter(value);

      this.column.valueFormatter = cellValue => valueFormatter({ value: cellValue });

      this.trigger$.next();
    }
  }

  public readonly column: GridColumnComponent<any, any>;

  private trigger$ = new Subject<void>();

  private updateWidth = () => {};

  private headerName = '';
  private groupHeaderName = '';

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
    @Host() @Optional() appColumn?: GridColumnComponent<any, any>,
    @Optional() @Inject(ROW_DATA) private rowData?: RowDataProvider<any>,
    @Optional() @Inject(GRID_ROW_DATA) private gridTableData?: GridRowDataProvider<any>,
  ) {
    if (appColumn) {
      this.column = appColumn;
    } else {
      throw new Error('No host column');
    }

    if (this.rowData) {
      this.subscriptions.push(
        merge(this.rowData.rowData$, this.trigger$).subscribe(() => this.updateWidth()),
      );
    } else if (this.gridTableData) {
      // this.subscriptions.push(
      //   merge(this.gridTableData.rowData$, this.trigger$)
      //     .pipe(debounceTime(300))
      //     .subscribe(() => this.updateWidth()),
      // );
    } else {
      throw new Error('Missing rowData, groupData, gridTableData and grid');
    }
  }

  ngOnInit() {
    this.trigger$.next();
  }

  ngDoCheck() {
    const currentHeaderName = this.column.headerName || '';
    const currentGroupHeaderName = ('group' in this.column && this.column.group?.headerName) || '';
    if (this.headerName !== currentHeaderName || this.groupHeaderName !== currentGroupHeaderName) {
      this.headerName = currentHeaderName;
      this.groupHeaderName = currentGroupHeaderName;
      this.trigger$.next();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private getValueFormatter(formatterType: FormatterType): (params) => string {
    switch (formatterType) {
      case 'currency':
        return currencyValueFormatter(this.locale, this.route);
      case 'date':
        return dateValueFormatter(this.locale, this.route);
      case 'percentage':
        return percentageValueFormatter(this.locale);
      case 'transactionType':
        return transactionTypeValueFormatter();
      case 'yesNo':
        return yesNoValueFormatter();
      case 'finYear':
        return finYearValueFormatter(this.locale);
      case 'decimal':
        return decimalValueFormatter(this.locale);
      case 'disposalMethod':
        return ({ value }) => {
          switch (value) {
            case 'WriteOff':
              return 'Write Off';
            case 'QuantitySale':
              return 'Sale (Partial)';
            case 'QuantityWriteOff':
              return 'Write Off (Partial)';
            case 'QuantityMixed':
              return 'Mixed (Partial)';
            default:
              return value;
          }
        };
      default:
        return ({ value }) => value?.toString() || '';
    }
  }
}
